let enabledCache: boolean | undefined

export const isReplayDiagnosticsEnabled = () => {
  if (enabledCache !== undefined) return enabledCache

  try {
    const search = typeof globalThis.location?.search === 'string' ? globalThis.location.search : ''
    const params = new URLSearchParams(search)
    enabledCache = params.get('replayDiagnostics') === '1' || params.get('replayDiag') === '1'
  } catch {
    enabledCache = false
  }

  return enabledCache
}

export const formatMs = (value: number | undefined) => {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '-'
}

type ReplayPerfMarker = {
  source: string
  wallTimeMs: number
  replayTimeMs?: number
  valueMs?: number
  count?: number
  detail?: string
}

type ReplayPerfSummaryEntry = {
  events: number
  count: number
  maxMs: number
  replayTimeMs?: number
  detail?: string
}

const replayPerfMarkers: ReplayPerfMarker[] = []
const replayPerfSummary = new Map<string, ReplayPerfSummaryEntry>()
const MAX_REPLAY_PERF_MARKERS = 256

const getReplayPacketTime = () => {
  const value = (globalThis as any).__replayPacketTimeMs
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export const recordReplayPerfMarker = (
  source: string,
  marker: Omit<ReplayPerfMarker, 'source' | 'wallTimeMs' | 'replayTimeMs'> & { replayTimeMs?: number } = {}
) => {
  if (!isReplayDiagnosticsEnabled()) return

  const recordedMarker: ReplayPerfMarker = {
    source,
    wallTimeMs: typeof performance === 'undefined' ? Date.now() : performance.now(),
    replayTimeMs: marker.replayTimeMs ?? getReplayPacketTime(),
    valueMs: marker.valueMs,
    count: marker.count,
    detail: marker.detail,
  }
  replayPerfMarkers.push(recordedMarker)

  const summary = replayPerfSummary.get(source) ?? {
    events: 0,
    count: 0,
    maxMs: 0,
  }
  summary.events++
  summary.count += recordedMarker.count ?? 0
  if (typeof recordedMarker.valueMs !== 'number' || recordedMarker.valueMs < summary.maxMs) {
    summary.replayTimeMs ??= recordedMarker.replayTimeMs
    summary.detail ??= recordedMarker.detail
  } else {
    summary.maxMs = recordedMarker.valueMs
    summary.replayTimeMs = recordedMarker.replayTimeMs
    summary.detail = recordedMarker.detail
  }
  replayPerfSummary.set(source, summary)

  if (replayPerfMarkers.length > MAX_REPLAY_PERF_MARKERS) {
    replayPerfMarkers.splice(0, replayPerfMarkers.length - MAX_REPLAY_PERF_MARKERS)
  }
}

const formatReplayPerfSummary = (entries: Map<string, ReplayPerfSummaryEntry>) => {
  return [...entries.entries()]
    .sort((a, b) => {
      return b[1].maxMs - a[1].maxMs || b[1].count - a[1].count || a[0].localeCompare(b[0])
    })
    .map(([source, entry]) => {
      const parts = [source]
      if (entry.events > 1) parts.push(`x${entry.events}`)
      if (entry.maxMs > 0) parts.push(`max=${formatMs(entry.maxMs)}ms`)
      if (entry.count > 0) parts.push(`count=${entry.count}`)
      if (typeof entry.replayTimeMs === 'number') parts.push(`at=${formatMs(entry.replayTimeMs / 1000)}s`)
      if (entry.detail) parts.push(entry.detail)
      return parts.join('/')
    })
    .join(',')
}

export const consumeReplayPerfMarkers = () => {
  if (!replayPerfMarkers.length) return '-'

  const batch = replayPerfMarkers.splice(0)
  const bySource = new Map<string, ReplayPerfSummaryEntry>()

  for (const marker of batch) {
    const entry = bySource.get(marker.source) ?? {
      events: 0,
      count: 0,
      maxMs: 0,
    }
    entry.events++
    entry.count += marker.count ?? 0
    if (typeof marker.valueMs === 'number') entry.maxMs = Math.max(entry.maxMs, marker.valueMs)
    entry.replayTimeMs = marker.replayTimeMs ?? entry.replayTimeMs
    entry.detail = marker.detail ?? entry.detail
    bySource.set(marker.source, entry)
  }

  return formatReplayPerfSummary(bySource)
}

export const resetReplayPerfSummary = () => {
  replayPerfMarkers.length = 0
  replayPerfSummary.clear()
}

export const getReplayPerfSummary = () => {
  return replayPerfSummary.size ? formatReplayPerfSummary(replayPerfSummary) : '-'
}
